import { Component, inject, Injector, Input, OnInit } from '@angular/core';
import { BaseModalComponent } from '@card-software/library';
import { AliasKey, Fabric } from '../../../models/item/fabric';
import { FabricProxyService } from '../../../proxies/item/fabric.proxy.service';
import { FormControl, Validators } from '@angular/forms';
import _ from 'lodash';

@Component({
  selector: 'app-fabric-alias-manage-modal',
  templateUrl: './fabric-alias-manage-modal.component.html',
  styleUrl: './fabric-alias-manage-modal.component.scss',
})
export class FabricAliasManageModalComponent extends BaseModalComponent<Fabric | null> implements OnInit {
  @Input() fabric: Fabric | null = null;
  @Input() customerReference = '';

  editableAliases: AliasKey[] = [];

  editingAlias: AliasKey | null = null;
  alias: FormControl<string | null> = new FormControl<string | null>(null, Validators.required);

  private readonly fabricProxyService = inject(FabricProxyService);

  constructor(injector: Injector) {
    super(injector);
  }

  /**
   * ModalService assigns the inputs with Object.assign before the first change detection,
   * so ngOnChanges never fires here: the existing aliases must be loaded in ngOnInit.
   */
  ngOnInit(): void {
    this.editableAliases = _.cloneDeep(this.getCustomerAliases());
  }

  saveAlias(): void {
    const aliasValue = this.alias.value ?? '';
    if (this.editingAlias) {
      const index = this.editableAliases.findIndex((alias) => alias.alias === this.editingAlias?.alias);
      if (index !== -1) {
        this.editableAliases[index].alias = aliasValue;
      }
    } else {
      const newAlias: AliasKey = {
        alias: aliasValue,
        customerReference: this.customerReference,
      };
      this.editableAliases.push(newAlias);
    }
    this.alias.setValue(null);
    this.editingAlias = null;
  }

  editAlias(alias: AliasKey): void {
    this.editingAlias = alias;
    this.alias.setValue(alias.alias);
  }

  cancelEdit(): void {
    this.editingAlias = null;
    this.alias.setValue(null);
  }

  //#region Actions
  onClose(): void {
    this.close({
      action: 'close',
      data: this.fabric,
    });
  }

  canSave(): boolean {
    if (_.isNil(this.fabric)) {
      return false;
    }
    const isSame = _.isEqual(this.getCustomerAliases(), this.editableAliases);
    return !isSame && this.editableAliases.length > 0;
  }

  onSave(): void {
    const fabric = this.fabric;
    if (_.isNil(fabric)) {
      return;
    }
    // Work on a copy so a failed PATCH leaves the fabric's aliases untouched
    const aliases = _.cloneDeep(fabric.aliases ?? []);
    //edit or add alias from editableAliases
    this.editableAliases.forEach((alias) => {
      const existingAlias = aliases.find((a) => a.alias === alias.alias && a.customerReference === alias.customerReference);
      if (existingAlias) {
        existingAlias.alias = alias.alias;
      } else {
        aliases.push(alias);
      }
    });

    this.fabricProxyService.updateAliases(fabric.id, aliases).subscribe({
      next: () => {
        fabric.aliases = aliases;
        this.close({
          action: 'save',
          data: fabric,
        });
      },
      error: (error: unknown) => {
        console.error('Error updating aliases:', error);
      },
    });
  }

  //#endregion

  private getCustomerAliases(): AliasKey[] {
    return this.fabric?.aliases?.filter((alias) => alias.customerReference === this.customerReference) ?? [];
  }
}
